import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, database } from './firebase';
import { ref, get, update } from 'firebase/database';

function useSuperAdmin() {
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) { setStatus('unauthorised'); return; }
      const token = await user.getIdTokenResult(true);
      setStatus(token.claims.superAdmin === true ? 'authorised' : 'unauthorised');
    });
    return () => unsubscribe();
  }, []);
  return status;
}

function StatCard({ label, value, accent }) {
  return (
    <div style={{
      backgroundColor: '#1c2640', border: `1px solid ${accent}44`,
      borderLeft: `3px solid ${accent}`, borderRadius: 10,
      padding: '16px 20px', flex: 1, minWidth: 100,
    }}>
      <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent, fontFamily: "'Outfit', sans-serif" }}>{value ?? '—'}</div>
    </div>
  );
}

function Section({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ backgroundColor: '#151c2e', borderRadius: 14, border: '1px solid #2a3555', marginBottom: 20, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', cursor: 'pointer', borderBottom: open ? '1px solid #2a3555' : 'none' }}>
        <span style={{ fontSize: 16, fontWeight: 600, fontFamily: "'Outfit', sans-serif" }}>{title}</span>
        <span style={{ color: '#5a6478', fontSize: 13 }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {open && <div style={{ padding: '16px 24px', overflowX: 'auto' }}>{children}</div>}
    </div>
  );
}

function Badge({ value, map }) {
  const config = map[value] || { color: '#8b95a8', label: value };
  return (
    <span style={{ backgroundColor: config.color + '22', color: config.color, border: `1px solid ${config.color}44`, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
      {config.label || value}
    </span>
  );
}

function Table({ cols, rows, emptyMsg = 'No data this period' }) {
  if (!rows || rows.length === 0) return <p style={{ color: '#5a6478', fontSize: 14, margin: 0 }}>{emptyMsg}</p>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          {cols.map(c => (
            <th key={c.key} style={{ textAlign: 'left', padding: '8px 12px', color: '#8b95a8', borderBottom: '1px solid #2a3555', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #1c2640' }}>
            {cols.map(c => (
              <td key={c.key} style={{ padding: '10px 12px', color: '#d1d5db', whiteSpace: c.wrap ? 'normal' : 'nowrap', maxWidth: c.wrap ? 280 : 'none', wordBreak: c.wrap ? 'break-word' : 'normal' }}>
                {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Expandable table — supports a renderDetail(row) prop for drill-down rows
function ExpandableTable({ cols, rows, emptyMsg = 'No data this period', renderDetail }) {
  const [expandedRows, setExpandedRows] = useState({});
  const toggle = (i) => setExpandedRows(prev => ({ ...prev, [i]: !prev[i] }));

  if (!rows || rows.length === 0) return <p style={{ color: '#5a6478', fontSize: 14, margin: 0 }}>{emptyMsg}</p>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          <th style={{ width: 28, borderBottom: '1px solid #2a3555' }} />
          {cols.map(c => (
            <th key={c.key} style={{ textAlign: 'left', padding: '8px 12px', color: '#8b95a8', borderBottom: '1px solid #2a3555', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <React.Fragment key={i}>
            <tr
              onClick={() => toggle(i)}
              style={{ borderBottom: expandedRows[i] ? 'none' : '1px solid #1c2640', cursor: 'pointer', backgroundColor: expandedRows[i] ? '#1a2236' : 'transparent' }}
            >
              <td style={{ padding: '10px 8px 10px 12px', color: '#5a6478', fontSize: 11, userSelect: 'none' }}>
                {expandedRows[i] ? '▼' : '▶'}
              </td>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '10px 12px', color: '#d1d5db', whiteSpace: c.wrap ? 'normal' : 'nowrap', maxWidth: c.wrap ? 280 : 'none', wordBreak: c.wrap ? 'break-word' : 'normal' }}>
                  {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
            {expandedRows[i] && (
              <tr style={{ borderBottom: '1px solid #1c2640' }}>
                <td />
                <td colSpan={cols.length} style={{ padding: '0 12px 16px 12px', backgroundColor: '#1a2236' }}>
                  {renderDetail(row)}
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

// Detail panel for match assignments
function MatchDetail({ row }) {
  const sent = row.results?.sent || [];
  const skipped = row.results?.skipped || [];
  const errors = row.results?.errors || [];

  function PlayerOutcome({ player }) {
    if (!player) return null;
    const wasSent = sent.some(p => p.name === player.name);
    const wasSkipped = skipped.find(p => p.name === player.name);
    const hadError = errors.find(p => p.name === player.name);
    return (
      <div style={{ marginBottom: 6 }}>
        <span style={{ color: '#d1d5db', fontSize: 13, fontWeight: 600 }}>{player.name || '—'}</span>
        <span style={{ color: '#5a6478', fontSize: 11, marginLeft: 6 }}>ID: {player.playerId || '—'}</span>
        <div style={{ marginTop: 2 }}>
          {wasSent && <span style={{ color: '#10b981', fontSize: 12, fontWeight: 600 }}>✓ Notified</span>}
          {wasSkipped && <span style={{ color: '#f59e0b', fontSize: 12 }}>⚠ Skipped — {wasSkipped.reason}</span>}
          {hadError && <span style={{ color: '#ef4444', fontSize: 12 }}>✗ Error — {hadError.error}</span>}
          {!wasSent && !wasSkipped && !hadError && <span style={{ color: '#5a6478', fontSize: 12 }}>— No record</span>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', paddingTop: 8 }}>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Tournament</div>
        <div style={{ color: '#d1d5db', fontSize: 13 }}>{row.tournamentName || row.tournamentId || '—'}</div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Table</div>
        <div style={{ color: '#d1d5db', fontSize: 13 }}>
          {row.tableNumber ? `Table ${row.tableNumber}` : '—'}
          {row.tableName ? <span style={{ color: '#8b95a8' }}> ({row.tableName})</span> : null}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Player A</div>
        <PlayerOutcome player={row.playerA} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Player B</div>
        <PlayerOutcome player={row.playerB} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Key</div>
        <div style={{ color: '#5a6478', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>{row.key}</div>
      </div>
    </div>
  );
}

// Detail panel for draw & reminder notifications
function NotifDetail({ row }) {
  const label = (text) => (
    <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{text}</div>
  );
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', paddingTop: 8 }}>
      {row.tournamentName && (
        <div>
          {label('Tournament')}
          <div style={{ color: '#d1d5db', fontSize: 13, fontWeight: 600 }}>{row.tournamentName}</div>
        </div>
      )}
      {row.organiserName && (
        <div>
          {label('Organiser')}
          <div style={{ color: '#d1d5db', fontSize: 13 }}>{row.organiserName}</div>
        </div>
      )}
      {row.playerName && (
        <div>
          {label('Player')}
          <div style={{ color: '#d1d5db', fontSize: 13 }}>{row.playerName}</div>
        </div>
      )}
      {row.recipients && row.recipients.length > 0 && (
        <div>
          {label(`Recipients (${row.recipients.length})`)}
          <div style={{ color: '#10b981', fontSize: 13 }}>
            {row.recipients.map((name, i) => (
              <div key={i}>✓ {name}</div>
            ))}
          </div>
          {row.skipped > 0 && (
            <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 4 }}>⚠ {row.skipped} skipped (no app installed)</div>
          )}
        </div>
      )}
      {row.opponentName && (
        <div>
          {label('Opponent')}
          <div style={{ color: '#d1d5db', fontSize: 13 }}>{row.opponentName}</div>
        </div>
      )}
      {row.roundName && (
        <div>
          {label('Round')}
          <div style={{ color: '#d1d5db', fontSize: 13 }}>{row.roundName}</div>
        </div>
      )}
      {row.reason && (
        <div>
          {label('Reason Not Sent')}
          <div style={{ color: '#f59e0b', fontSize: 13 }}>{
            row.reason === 'no_premium_players' ? 'No premium players in this tournament' :
            row.reason === 'player_not_registered' ? 'Player not found in tournament draw' :
            row.reason
          }</div>
        </div>
      )}
      <div>
        {label('Key')}
        <div style={{ color: '#5a6478', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>{row.key}</div>
      </div>
    </div>
  );
}

// Detail panel for ad hoc messages
function AdHocDetail({ row }) {
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', paddingTop: 8 }}>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Full Message</div>
        <div style={{ color: '#d1d5db', fontSize: 13, maxWidth: 500 }}>{row.message || '—'}</div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Organiser ID</div>
        <div style={{ color: '#5a6478', fontSize: 12, fontFamily: 'monospace' }}>{row.organiserId}</div>
      </div>
      {row.tournamentId && (
        <div>
          <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Tournament ID</div>
          <div style={{ color: '#d1d5db', fontSize: 13 }}>{row.tournamentId}</div>
        </div>
      )}
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Result</div>
        <div style={{ fontSize: 13 }}>
          <span style={{ color: '#10b981', fontWeight: 600 }}>✓ {row.sent ?? 0} sent</span>
          {' · '}
          <span style={{ color: '#f59e0b' }}>⚠ {row.skipped ?? 0} skipped</span>
          {' · '}
          <span style={{ color: '#d1d5db' }}>{row.targetCount ?? 0} targeted</span>
        </div>
      </div>
    </div>
  );
}

function FeedbackDetail({ row, onStatusChange }) {
  const [updating, setUpdating] = useState(false);

  async function setStatus(newStatus) {
    setUpdating(true);
    try {
      await update(ref(database, `feedback/${row.id}`), { status: newStatus });
      onStatusChange(row.id, newStatus);
    } catch (e) {
      console.error('Failed to update status:', e);
    } finally {
      setUpdating(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', paddingTop: 8 }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Message</div>
        <div style={{ color: '#d1d5db', fontSize: 13, lineHeight: 1.5 }}>{row.message || '—'}</div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Device</div>
        <div style={{ color: '#d1d5db', fontSize: 13 }}>{row.deviceName || '—'}</div>
        <div style={{ color: '#5a6478', fontSize: 12 }}>{row.deviceModel || ''}</div>
        <div style={{ color: '#5a6478', fontSize: 12 }}>OS: {row.osVersion || '—'}</div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Reply To</div>
        {row.email
          ? <a href={`mailto:${row.email}`} style={{ color: '#3b82f6', fontSize: 13, textDecoration: 'none' }}>{row.email} ✉</a>
          : <div style={{ color: '#5a6478', fontSize: 13 }}>No email</div>
        }
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Player ID</div>
        <div style={{ color: '#5a6478', fontSize: 12, fontFamily: 'monospace' }}>{row.playerId || '—'}</div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Update Status</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['new', 'read', 'resolved'].map(s => (
            <button key={s} onClick={() => setStatus(s)} disabled={updating || row.status === s}
              style={{
                padding: '4px 10px', fontSize: 12, borderRadius: 6, cursor: row.status === s ? 'default' : 'pointer', border: 'none', fontWeight: 600,
                backgroundColor: row.status === s ? '#2a3555' : s === 'resolved' ? '#10b98122' : s === 'read' ? '#3b82f622' : '#f59e0b22',
                color: row.status === s ? '#5a6478' : s === 'resolved' ? '#10b981' : s === 'read' ? '#3b82f6' : '#f59e0b',
              }}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const adminStatus = useSuperAdmin();
  const isMobile = window.innerWidth < 768;
  const [reports, setReports] = useState({});
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [feedbackSummary, setFeedbackSummary] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(true);

  useEffect(() => {
    if (adminStatus !== 'authorised') return;
    get(ref(database, 'adminReports/weeklyAudit')).then(snap => {
      if (snap.exists()) {
        const data = snap.val();
        const sorted = Object.keys(data).sort().reverse();
        setReports(data);
        setDates(sorted);
        setSelectedDate(sorted[0]);
      }
    }).catch(e => console.error(e)).finally(() => setLoading(false));

    // Fetch live feedback from Cloud Function
    fetch('https://us-central1-cue-clock-pro.cloudfunctions.net/getFeedbackReport', {
      headers: { 'x-admin-key': 'cue-clock-pro-admin-2026' }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setFeedback(data.items || []);
          setFeedbackSummary(data.summary || null);
        }
      })
      .catch(e => console.error('Feedback fetch error:', e))
      .finally(() => setFeedbackLoading(false));
  }, [adminStatus]);

  const centreStyle = {
    minHeight: '100vh', backgroundColor: '#0d1321', color: '#f0f2f5',
    fontFamily: "'DM Sans', sans-serif", display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  };

  if (adminStatus === 'loading' || (adminStatus === 'authorised' && loading)) {
    return (
      <div style={centreStyle}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ width: 36, height: 36, border: '3px solid #1c2640', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: '#8b95a8', marginTop: 16 }}>{adminStatus === 'loading' ? 'Checking permissions...' : 'Loading reports...'}</p>
      </div>
    );
  }

  if (adminStatus === 'unauthorised') {
    return (
      <div style={centreStyle}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ backgroundColor: '#151c2e', borderRadius: 14, padding: 48, maxWidth: 400, textAlign: 'center', border: '1px solid #2a3555' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, fontFamily: "'Outfit', sans-serif", marginBottom: 8 }}>Access Denied</h2>
          <p style={{ color: '#8b95a8', marginBottom: 24 }}>Super admin access required.</p>
          <button onClick={() => navigate('/organiser-login')} style={{ width: '100%', padding: '12px 24px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Sign In</button>
          <button onClick={() => navigate('/')} style={{ width: '100%', padding: '12px 24px', backgroundColor: '#1c2640', color: '#8b95a8', border: '1px solid #2a3555', borderRadius: 10, cursor: 'pointer', fontSize: 14 }}>← Back to Home</button>
        </div>
      </div>
    );
  }

  const report = selectedDate ? reports[selectedDate] : null;
  const s = report?.summary;
  const d = report?.detail;

  function updateFeedbackStatus(id, newStatus) {
    setFeedback(prev => prev.map(f => f.id === id ? { ...f, status: newStatus } : f));
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0d1321', color: '#f0f2f5', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ backgroundColor: '#151c2e', borderBottom: '1px solid #2a3555', padding: '16px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <img src="/icon.png" alt="Cue Clock Pro" style={{ width: 40, height: 40, borderRadius: 10 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}>Cue Clock Pro</div>
            <div style={{ fontSize: 13, color: '#8b95a8' }}>Admin Dashboard · Weekly Audit</div>
          </div>
          {dates.length > 0 && (
            <select value={selectedDate || ''} onChange={e => setSelectedDate(e.target.value)}
              style={{ padding: '8px 12px', backgroundColor: '#1c2640', color: '#f0f2f5', border: '1px solid #2a3555', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
              {dates.map(d => <option key={d} value={d}>Report: {d}</option>)}
            </select>
          )}
          <button onClick={() => navigate('/organiser-dashboard')}
            style={{ padding: '8px 16px', backgroundColor: '#1c2640', color: '#8b95a8', border: '1px solid #2a3555', borderRadius: 10, cursor: 'pointer', fontSize: 14 }}>
            🏠 Dashboard
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '16px' : '24px' }}>
        {!report ? (
          <div style={{ backgroundColor: '#151c2e', borderRadius: 14, border: '1px solid #2a3555', padding: 48, textAlign: 'center' }}>
            <p style={{ color: '#8b95a8' }}>No audit reports found. Run the weekly audit to generate one.</p>
          </div>
        ) : (
          <>
            {/* Meta */}
            <div style={{ marginBottom: 20, fontSize: 13, color: '#8b95a8', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <span>Generated: <span style={{ color: '#d1d5db' }}>{report.generatedAt}</span></span>
              <span>·</span>
              <span>Coverage: <span style={{ color: '#d1d5db' }}>{report.weekCoverage}</span></span>
              {report.triggeredManually && <span style={{ backgroundColor: '#1e3a5f', color: '#60a5fa', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Manual Run</span>}
            </div>

            {/* Players summary */}
            <div style={{ fontSize: 11, color: '#5a6478', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Players</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <StatCard label="Total" value={s?.players?.total} accent="#3b82f6" />
              <StatCard label="Active" value={s?.players?.active} accent="#10b981" />
              <StatCard label="Expired" value={s?.players?.expired} accent="#ef4444" />
              <StatCard label="New This Week" value={s?.players?.newThisWeek} accent="#f59e0b" />
              <StatCard label="No App" value={s?.playersWithoutApp} accent="#ef4444" />
            </div>

            {/* Tournaments summary */}
            <div style={{ fontSize: 11, color: '#5a6478', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Tournaments</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <StatCard label="Tracked" value={s?.tournaments?.total} accent="#a78bfa" />
              <StatCard label="Live" value={s?.tournaments?.live} accent="#10b981" />
              <StatCard label="Upcoming" value={s?.tournaments?.upcoming} accent="#f59e0b" />
            </div>

            {/* Notifications summary */}
            <div style={{ fontSize: 11, color: '#5a6478', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Notifications This Week</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
              <StatCard label="Total" value={s?.notifications?.total} accent="#3b82f6" />
              <StatCard label="Match Alerts" value={s?.notifications?.matchAssigned} accent="#f59e0b" />
              <StatCard label="Draw" value={s?.notifications?.drawPublished} accent="#a78bfa" />
              <StatCard label="Reminders" value={s?.notifications?.tournamentReminder} accent="#34d399" />
              <StatCard label="Ad Hoc" value={s?.notifications?.adHoc} accent="#fb923c" />
            </div>

            {/* No-app warning */}
            {d?.playersWithoutApp?.length > 0 && (
              <div style={{ backgroundColor: '#2d1515', border: '1px solid #7f1d1d', borderRadius: 10, padding: '12px 20px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: 600, color: '#fca5a5', marginBottom: 4 }}>
                    {d.playersWithoutApp.length} active player{d.playersWithoutApp.length > 1 ? 's' : ''} without the app installed
                  </div>
                  <div style={{ fontSize: 13, color: '#8b95a8' }}>{d.playersWithoutApp.map(p => p.name).join(', ')}</div>
                </div>
              </div>
            )}

            {/* Detail sections */}
            <Section title="👤 Registered Players">
              <Table cols={[
                { key: 'name', label: 'Name' },
                { key: 'playerId', label: 'CueScore ID' },
                { key: 'status', label: 'Status', render: v => <Badge value={v} map={{ active: { color: '#10b981', label: '✓ Active' }, expired: { color: '#ef4444', label: '✗ Expired' } }} /> },
                { key: 'hasAppInstalled', label: 'App', render: v => v ? <span style={{ color: '#10b981', fontWeight: 600 }}>✓ Installed</span> : <span style={{ color: '#ef4444', fontWeight: 600 }}>✗ Missing</span> },
                { key: 'registeredDate', label: 'Registered' },
                { key: 'expiryDate', label: 'Expires' },
                { key: 'newThisWeek', label: 'New', render: v => v ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>🆕</span> : '' },
              ]} rows={d?.players || []} emptyMsg="No players registered yet" />
            </Section>

            <Section title="🏆 Tracked Tournaments">
              <Table cols={[
                { key: 'name', label: 'Tournament', wrap: true },
                { key: 'tournamentId', label: 'ID' },
                { key: 'status', label: 'Status', render: v => <Badge value={v} map={{ live: { color: '#10b981', label: '● Live' }, upcoming: { color: '#f59e0b', label: '◔ Upcoming' }, completed: { color: '#8b95a8', label: '✓ Completed' } }} /> },
                { key: 'startDate', label: 'Start Date' },
                { key: 'organiserId', label: 'Organiser ID' },
              ]} rows={d?.tournaments || []} emptyMsg="No tournaments tracked" />
            </Section>

            <Section title="🎱 Match Assignments This Week" defaultOpen={false}>
              <p style={{ color: '#5a6478', fontSize: 12, marginTop: 0, marginBottom: 12 }}>Click a row to see who was notified</p>
                <ExpandableTable cols={[
                { key: 'sentAt', label: 'Sent At' },
                { key: 'tournamentName', label: 'Tournament', wrap: true, render: (v, row) => v || row.tournamentId || '—' },
                { key: 'matchId', label: 'Match ID' },
                { key: 'tableNumber', label: 'Table', render: v => v ? `Table ${v}` : '—' },
                { key: 'source', label: 'Source' },
              ]} rows={d?.matchAssignments || []} emptyMsg="No match assignments this week"
                renderDetail={row => <MatchDetail row={row} />}
              />
            </Section>

            <Section title="🎯 Draw & Reminder Notifications" defaultOpen={false}>
              <p style={{ color: '#5a6478', fontSize: 12, marginTop: 0, marginBottom: 12 }}>Click a row to see full detail</p>
              <ExpandableTable cols={[
                { key: 'sentAt', label: 'Sent At' },
                { key: 'type', label: 'Type', render: v => <Badge value={v} map={{ draw_published: { color: '#a78bfa', label: 'Draw' }, tournament_reminder: { color: '#34d399', label: 'Reminder' } }} /> },
                { key: 'sent', label: 'Sent', render: v => v === null || v === undefined ? '—' : v === true || v === 1 ? <span style={{color:'#10b981',fontWeight:600}}>✓ Yes</span> : v === false || v === 0 ? <span style={{color:'#ef4444'}}>✗ No</span> : v },
                { key: 'skipped', label: 'Skipped' },
                { key: 'playerCount', label: 'Players' },
                { key: 'reason', label: 'Reason' },
              ]} rows={d?.notificationsSent || []} emptyMsg="No draw or reminder notifications this week"
                renderDetail={row => <NotifDetail row={row} />}
              />
            </Section>

            <Section title="📬 Feedback" defaultOpen={false}>
              {feedbackLoading ? (
                <p style={{ color: '#8b95a8', fontSize: 14 }}>Loading feedback...</p>
              ) : (
                <>
                  {feedbackSummary && (
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                      <StatCard label="Total" value={feedbackSummary.total} accent="#3b82f6" />
                      <StatCard label="New" value={feedbackSummary.new} accent="#f59e0b" />
                      <StatCard label="Bugs" value={feedbackSummary.bugs} accent="#ef4444" />
                      <StatCard label="Features" value={feedbackSummary.features} accent="#a78bfa" />
                      <StatCard label="Questions" value={feedbackSummary.questions} accent="#34d399" />
                    </div>
                  )}
                  <ExpandableTable
                    cols={[
                      { key: 'createdAtReadable', label: 'Received' },
                      { key: 'type', label: 'Type', render: v => <Badge value={v} map={{
                        bug: { color: '#ef4444', label: '🐛 Bug' },
                        feature: { color: '#a78bfa', label: '✨ Feature' },
                        question: { color: '#34d399', label: '❓ Question' },
                      }} /> },
                      { key: 'playerName', label: 'Player', render: (v, row) => v
                        ? row.email
                          ? <a href={`mailto:${row.email}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>{v} ✉</a>
                          : v
                        : <span style={{ color: '#5a6478' }}>Anonymous</span>
                      },
                      { key: 'appVersion', label: 'Version' },
                      { key: 'platform', label: 'Platform', render: v => v === 'ios' ? '🍎 iOS' : v === 'android' ? '🤖 Android' : v || '—' },
                      { key: 'status', label: 'Status', render: v => <Badge value={v} map={{
                        new: { color: '#f59e0b', label: '● New' },
                        read: { color: '#3b82f6', label: '● Read' },
                        resolved: { color: '#10b981', label: '✓ Resolved' },
                      }} /> },
                    ]}
                    rows={feedback}
                    emptyMsg="No feedback in the last 30 days"
                    renderDetail={row => <FeedbackDetail row={row} onStatusChange={updateFeedbackStatus} />}
                  />
                </>
              )}
            </Section>

            <Section title="📢 Ad Hoc Messages" defaultOpen={false}>
              <p style={{ color: '#5a6478', fontSize: 12, marginTop: 0, marginBottom: 12 }}>Click a row to see full detail</p>
              <ExpandableTable cols={[
                { key: 'sentAt', label: 'Sent At' },
                { key: 'title', label: 'Title', wrap: true },
                { key: 'targetType', label: 'Target' },
                { key: 'targetCount', label: 'Targeted' },
                { key: 'sent', label: 'Sent' },
                { key: 'skipped', label: 'Skipped' },
              ]} rows={d?.adHocMessages || []} emptyMsg="No ad hoc messages this week"
                renderDetail={row => <AdHocDetail row={row} />}
              />
            </Section>
          </>
        )}
      </div>
    </div>
  );
}